import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronUp, Plus, Trash2, Zap } from 'lucide-react';

import {
  InvocationMethodDefinition,
  LLMInvocationMethod,
  LLMModelInvocationProfileInput,
  LLMTaskTypeOption,
  ProviderNativeDefault,
} from '../../../types/admin';
import {
  BUILT_IN_METHOD_DEFINITIONS,
  METHOD_LABELS,
  NATIVE_SCHEMA_LABELS,
  providerNativeProfileDefaults,
} from './invocationMethodDefinitions';
import { ProviderNativeProfileFields } from './ProviderNativeProfileFields';
import { ProfileConfigField } from './ProfileConfigField';

export { BUILT_IN_METHOD_DEFINITIONS } from './invocationMethodDefinitions';

export interface ModelInvocationTabProps {
  profiles: LLMModelInvocationProfileInput[];
  onChangeProfiles: (profiles: LLMModelInvocationProfileInput[]) => void;
  taskType?: LLMTaskTypeOption | null;
  modelCode: string;
  isModelActive: boolean;
  registryMethods?: InvocationMethodDefinition[];
  supportedBodyBuilderKeys?: string[];
  supportedParserKeys?: string[];
  supportedNativeBodyBuilderKeys?: string[];
  providerCode?: string;
  providerNativeDefaults?: ProviderNativeDefault[];
}

export const ModelInvocationTab: React.FC<ModelInvocationTabProps> = ({
  profiles,
  onChangeProfiles,
  taskType: _taskType,
  modelCode,
  isModelActive,
  registryMethods,
  supportedBodyBuilderKeys = [
    'openai_chat_completions',
    'openai_responses',
    'anthropic_messages',
    'cloudflare_run',
  ],
  supportedParserKeys = [
    'openai_chat_completions',
    'openai_responses',
    'anthropic_messages',
    'cloudflare_run',
  ],
  supportedNativeBodyBuilderKeys = [
    'native_messages',
    'native_responses',
    'native_gemini',
    'native_image_bytes',
  ],
  providerCode,
  providerNativeDefaults,
}) => {
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(0);
  const [selectedMethodToAdd, setSelectedMethodToAdd] = React.useState<string>('');

  const methodDefinitions = useMemo(() => {
    const map = new Map<string, InvocationMethodDefinition>();
    Object.values(BUILT_IN_METHOD_DEFINITIONS).forEach((def) => map.set(def.method, def));
    (registryMethods ?? []).forEach((def) => map.set(def.method, def));
    return map;
  }, [registryMethods]);

  const activeProfilesCount = useMemo(
    () => profiles.filter((p) => p.isActive !== false).length,
    [profiles]
  );

  const handleAddMethod = (method: LLMInvocationMethod) => {
    const def = methodDefinitions.get(method) ?? BUILT_IN_METHOD_DEFINITIONS[method];
    const existingSameMethod = profiles.filter((p) => p.method === method).length;
    const key = existingSameMethod > 0 ? `${method}_${existingSameMethod + 1}` : method;

    const newProfile: LLMModelInvocationProfileInput = {
      key,
      method,
      priority: 100,
      modes: ['cheap', 'normal', 'expensive'],
      supportsStream: def?.supportsStream ?? false,
      supportsTools: def?.supportsTools ?? false,
      supportsJsonMode: def?.supportsJsonMode ?? false,
      supportsReasoning: def?.supportsReasoning ?? false,
      isDefault: profiles.length === 0,
      isActive: true,
      ...(method === 'gateway_provider_native'
        ? providerNativeProfileDefaults(providerCode, providerNativeDefaults)
        : {
            providerSlug: null,
            endpointPath: null,
            providerModelCode: null,
            bodyBuilderKey: null,
            parserKey: null,
          }),
    };

    const nextProfiles = [...profiles, newProfile];
    onChangeProfiles(nextProfiles);
    setExpandedIndex(nextProfiles.length - 1);
    setSelectedMethodToAdd('');
  };

  const handleRemoveProfile = (index: number) => {
    const removingDefault = profiles[index]?.isDefault;
    const nextProfiles = profiles.filter((_, i) => i !== index);
    if (removingDefault && nextProfiles.length > 0) {
      nextProfiles[0] = { ...nextProfiles[0], isDefault: true };
    }
    onChangeProfiles(nextProfiles);
    if (expandedIndex === index) {
      setExpandedIndex(nextProfiles.length > 0 ? 0 : null);
    } else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const handleUpdateProfile = (index: number, patch: Partial<LLMModelInvocationProfileInput>) => {
    let nextProfiles = profiles.map((p, i) => (i === index ? { ...p, ...patch } : p));
    if (patch.isDefault) {
      nextProfiles = nextProfiles.map((p, i) => ({
        ...p,
        isDefault: i === index,
      }));
    }
    onChangeProfiles(nextProfiles);
  };

  const availableMethodsToAdd = useMemo(() => {
    const configuredMethods = new Set(profiles.map((p) => p.method));
    return (Object.keys(BUILT_IN_METHOD_DEFINITIONS) as LLMInvocationMethod[]).filter(
      (m) => m === 'gateway_provider_native' || !configuredMethods.has(m)
    );
  }, [profiles]);

  return (
    <Box className="mt-2 space-y-4">
      {/* Alert when active model has no active profile */}
      {isModelActive && activeProfilesCount === 0 && (
        <Alert severity="error" className="items-center">
          <Typography variant="body2" className="font-semibold">
            Active model requires at least one active invocation profile.
          </Typography>
          <Typography variant="caption" className="text-gray-600 dark:text-gray-300">
            Select an invocation method below to define how this model can be called by the LLM
            Gateway.
          </Typography>
        </Alert>
      )}

      {/* Header & Quick Add Method Section */}
      <Box className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/50">
        <Box>
          <Typography
            variant="subtitle2"
            className="font-semibold text-gray-900 dark:text-slate-100"
          >
            Invocation Methods ({profiles.length})
          </Typography>
          <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
            Define typed endpoints supported by this model. Gateway selects the best supported
            method deterministically.
          </Typography>
        </Box>

        <Box className="flex items-center gap-2">
          <FormControl size="small" className="min-w-[220px]">
            <InputLabel>Add Method</InputLabel>
            <Select
              label="Add Method"
              value={selectedMethodToAdd}
              onChange={(e) => {
                const method = e.target.value as LLMInvocationMethod;
                if (method) {
                  handleAddMethod(method);
                }
              }}
            >
              <MenuItem value="" disabled>
                <em>Select a method to add...</em>
              </MenuItem>
              {availableMethodsToAdd.map((m) => (
                <MenuItem key={m} value={m}>
                  {METHOD_LABELS[m]?.label ?? m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Profiles List */}
      {profiles.length === 0 ? (
        <Box className="rounded-xl border border-dashed border-slate-300 p-8 text-center dark:border-slate-700">
          <Zap className="mx-auto mb-2 text-slate-400" size={32} />
          <Typography variant="body1" className="font-medium text-gray-700 dark:text-slate-300">
            No invocation methods configured yet
          </Typography>
          <Typography variant="body2" className="mb-4 text-gray-500 dark:text-slate-400">
            Choose supported methods such as Cloudflare AI Binding, REST /ai/run, or OpenAI Chat
            Completions.
          </Typography>
          <Box className="flex flex-wrap justify-center gap-2">
            {(
              ['ai_binding', 'cloudflare_run', 'openai_chat_completions'] as LLMInvocationMethod[]
            ).map((method) => (
              <Button
                key={method}
                variant="outlined"
                size="small"
                startIcon={<Plus size={14} />}
                onClick={() => handleAddMethod(method)}
              >
                {METHOD_LABELS[method]?.label ?? method}
              </Button>
            ))}
          </Box>
        </Box>
      ) : (
        <Box className="space-y-3">
          {profiles.map((profile, index) => {
            const method = profile.method as LLMInvocationMethod;
            const def =
              methodDefinitions.get(profile.method) ?? BUILT_IN_METHOD_DEFINITIONS[method];
            const isExpanded = expandedIndex === index;
            const isCfModel = modelCode?.startsWith('@cf/');
            const isAnthropicOnCf = isCfModel && method === 'anthropic_messages';

            return (
              <Card
                key={`profile-${profile.key}-${index}`}
                variant="outlined"
                className={`overflow-hidden transition-all ${
                  profile.isActive === false
                    ? 'bg-slate-50/50 opacity-60 dark:bg-slate-900/30'
                    : profile.isDefault
                      ? 'border-blue-300 bg-blue-50/20 dark:border-blue-800 dark:bg-blue-950/10'
                      : 'border-slate-200 dark:border-slate-800'
                }`}
              >
                {/* Profile Header Bar */}
                <Box className="flex items-center justify-between p-3">
                  <Box className="flex flex-wrap items-center gap-3">
                    <Tooltip
                      title={
                        profile.isDefault
                          ? 'Default method for this model'
                          : 'Set as default method'
                      }
                    >
                      <FormControlLabel
                        control={
                          <Radio
                            size="small"
                            checked={Boolean(profile.isDefault)}
                            onChange={() => handleUpdateProfile(index, { isDefault: true })}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            className="font-semibold text-gray-700 dark:text-slate-300"
                          >
                            Default
                          </Typography>
                        }
                        className="mr-1"
                      />
                    </Tooltip>

                    <Chip
                      size="small"
                      color={profile.isDefault ? 'primary' : 'default'}
                      label={METHOD_LABELS[method]?.label ?? profile.method}
                      className="font-medium"
                    />

                    <Typography
                      variant="body2"
                      className="font-mono text-xs text-gray-500 dark:text-slate-400"
                    >
                      key:{' '}
                      <strong className="text-gray-800 dark:text-slate-200">{profile.key}</strong>
                    </Typography>

                    {/* Registry Feature Badges */}
                    <Box className="flex items-center gap-1">
                      {profile.supportsStream && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="info"
                          label="Stream"
                          className="h-5 text-[10px]"
                        />
                      )}
                      {profile.supportsTools && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="secondary"
                          label="Tools"
                          className="h-5 text-[10px]"
                        />
                      )}
                      {profile.supportsJsonMode && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="success"
                          label="JSON"
                          className="h-5 text-[10px]"
                        />
                      )}
                      {profile.supportsReasoning && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          label="Reasoning"
                          className="h-5 text-[10px]"
                        />
                      )}
                    </Box>
                  </Box>

                  <Box className="flex items-center gap-2">
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={profile.isActive !== false}
                          onChange={(e) =>
                            handleUpdateProfile(index, { isActive: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Typography variant="caption">
                          {profile.isActive !== false ? 'Active' : 'Disabled'}
                        </Typography>
                      }
                    />

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveProfile(index)}
                      title="Remove method"
                    >
                      <Trash2 size={16} />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => setExpandedIndex(isExpanded ? null : index)}
                      title={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Collapsible Content */}
                <Collapse in={isExpanded}>
                  <Divider />
                  <CardContent className="space-y-4 pt-4">
                    {isAnthropicOnCf && (
                      <Alert severity="warning" className="text-xs">
                        Warning: Cloudflare (@cf/) models typically do not support Anthropic
                        Messages REST endpoints.
                      </Alert>
                    )}

                    {/* Method Overview & Read-only format description */}
                    <Box className="rounded-lg bg-slate-100 p-3 dark:bg-slate-800/60">
                      <Typography
                        variant="caption"
                        className="font-semibold text-gray-700 dark:text-slate-300"
                      >
                        Method Envelope & Transport:
                      </Typography>
                      <Typography
                        variant="body2"
                        className="mt-1 font-mono text-xs text-slate-600 dark:text-slate-400"
                      >
                        {def?.transport === 'binding' &&
                          'Transport: In-worker direct env.AI.run(modelCode, taskInput)'}
                        {def?.transport === 'cloudflare_rest' &&
                          `Transport: Cloudflare REST ${def.endpoint ?? ''}`}
                        {def?.transport === 'gateway_provider_native' &&
                          `Transport: https://gateway.ai.cloudflare.com/v1/{account}/{gateway}/${profile.providerSlug || '{provider}'}/${profile.endpointPath || '{endpoint}'}`}
                      </Typography>
                      <Typography
                        variant="caption"
                        className="mt-1 block text-gray-500 dark:text-slate-400"
                      >
                        {METHOD_LABELS[method]?.desc}
                      </Typography>
                    </Box>

                    {/* Basic Profile Config Fields */}
                    <Box className="grid grid-cols-1 gap-3">
                      <TextField
                        size="small"
                        label="Profile Key"
                        value={profile.key}
                        onChange={(e) => handleUpdateProfile(index, { key: e.target.value })}
                        helperText="Unique identifier within this model"
                        required
                      />
                    </Box>

                    {/* Cost Tier & Priority Note */}
                    <Typography
                      variant="caption"
                      className="block text-slate-500 dark:text-slate-400"
                    >
                      <em>Lưu ý:</em> Phân loại Cost Tier (cheap / normal / expensive) và Độ ưu tiên
                      định tuyến (Priority) được cấu hình khi gán model vào capability tại màn{' '}
                      <strong>Capability Management</strong>. Invocation method này mặc định sẵn
                      sàng phục vụ cho model.
                    </Typography>

                    {/* Capabilities & Advanced Feature Toggles */}
                    <Box className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
                      <Typography
                        variant="caption"
                        className="font-semibold text-gray-700 dark:text-slate-300"
                      >
                        Capabilities & Feature Support:
                      </Typography>
                      <Box className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              disabled={!def?.supportsStream}
                              checked={Boolean(profile.supportsStream && def?.supportsStream)}
                              onChange={(e) =>
                                handleUpdateProfile(index, { supportsStream: e.target.checked })
                              }
                            />
                          }
                          label={<Typography variant="caption">Streaming</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              disabled={!def?.supportsTools}
                              checked={Boolean(profile.supportsTools && def?.supportsTools)}
                              onChange={(e) =>
                                handleUpdateProfile(index, { supportsTools: e.target.checked })
                              }
                            />
                          }
                          label={<Typography variant="caption">Tool Calling</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              disabled={!def?.supportsJsonMode}
                              checked={Boolean(profile.supportsJsonMode && def?.supportsJsonMode)}
                              onChange={(e) =>
                                handleUpdateProfile(index, { supportsJsonMode: e.target.checked })
                              }
                            />
                          }
                          label={<Typography variant="caption">JSON Mode</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              disabled={!def?.supportsReasoning}
                              checked={Boolean(profile.supportsReasoning && def?.supportsReasoning)}
                              onChange={(e) =>
                                handleUpdateProfile(index, { supportsReasoning: e.target.checked })
                              }
                            />
                          }
                          label={<Typography variant="caption">Reasoning</Typography>}
                        />
                      </Box>
                    </Box>

                    {/* Request body builder for binding / /ai/run (model page → "Usage") */}
                    {(method === 'ai_binding' || method === 'cloudflare_run') && (
                      <Box className="rounded-lg border border-sky-300 bg-sky-50/40 p-3 dark:border-sky-800 dark:bg-sky-950/20">
                        <FormControl size="small" fullWidth>
                          <InputLabel>Request body builder</InputLabel>
                          <Select
                            label="Request body builder"
                            value={profile.bodyBuilderKey ?? ''}
                            onChange={(e) =>
                              handleUpdateProfile(index, {
                                bodyBuilderKey: e.target.value ? e.target.value : null,
                                parserKey: null,
                              })
                            }
                          >
                            <MenuItem value="">
                              <em>Auto (infer from model code)</em>
                            </MenuItem>
                            {supportedNativeBodyBuilderKeys.map((key) => (
                              <MenuItem key={key} value={key}>
                                {NATIVE_SCHEMA_LABELS[key] ?? key}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            How the gateway shapes the request body for this transport (binding or
                            /ai/run). Auto infers it from the model code, which is right for @cf/*
                            and OpenAI-style chat models. For media models (video, image), put the
                            input fields in the Input tab instead.
                          </FormHelperText>
                        </FormControl>
                      </Box>
                    )}

                    {/* Provider-Native Specific Configuration */}
                    {method === 'gateway_provider_native' && (
                      <ProviderNativeProfileFields
                        profile={profile}
                        supportedBodyBuilderKeys={supportedBodyBuilderKeys}
                        supportedParserKeys={supportedParserKeys}
                        onUpdate={(patch) => handleUpdateProfile(index, patch)}
                      />
                    )}

                    <ProfileConfigField
                      profile={profile}
                      onUpdate={(patch) => handleUpdateProfile(index, patch)}
                    />
                  </CardContent>
                </Collapse>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
